/**
 * Phone formatting for US numbers: 10-digit (XXX) XXX-XXXX and
 * 11-digit with leading 1: 1 (XXX) XXX-XXXX (e.g. 1-800 numbers).
 */

const MAX_DIGITS_10 = 10;
const MAX_DIGITS_11 = 11;
const US_COUNTRY_CODE = '1';

/**
 * Extracts digits from a string and limits length for US format:
 * - If 11+ digits and first is 1, keep first 11 (1 + 10-digit number).
 * - Otherwise keep first 10 digits.
 */
export function getPhoneDigits(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length > MAX_DIGITS_11 && digits.startsWith(US_COUNTRY_CODE)) {
    return digits.slice(0, MAX_DIGITS_11);
  }
  if (digits.length > MAX_DIGITS_10) {
    if (digits.startsWith(US_COUNTRY_CODE)) {
      return digits.slice(0, MAX_DIGITS_11);
    }
    return digits.slice(0, MAX_DIGITS_10);
  }
  return digits;
}

/**
 * Formats digits as US phone display string.
 * - 10 digits: (XXX) XXX-XXXX
 * - 11 digits with leading 1: 1 (XXX) XXX-XXXX
 * - Partial input is formatted progressively (e.g. (900) 444-2).
 */
export function formatPhoneNumber(digits: string): string {
  if (digits.length === 0) return '';

  const len = digits.length;
  const hasCountryCode =
    len === MAX_DIGITS_11 && digits.charAt(0) === US_COUNTRY_CODE;

  if (hasCountryCode) {
    const area = digits.slice(1, 4);
    const mid = digits.slice(4, 7);
    const last = digits.slice(7, 11);
    return `1 (${area}) ${mid}-${last}`;
  }

  // 10-digit or partial
  const take = Math.min(len, MAX_DIGITS_10);
  const d = digits.slice(0, take);

  if (d.length < 3) return d;
  if (d.length === 3) return `(${d})`;
  if (d.length <= 6) {
    return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  }
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}

/**
 * Format a stored phone (+1XXXXXXXXXX) for display in tel inputs.
 */
export function phoneNumberFromStorage(stored: string): string {
  if (!stored?.trim()) return '';
  return formatPhoneNumber(getPhoneDigits(stored));
}

/**
 * Normalize display/raw input to DB format (+1XXXXXXXXXX or +1XXXXXXXXXXX).
 */
export function normalizePhoneForStorage(displayOrRaw: string): string {
  const digits = getPhoneDigits(displayOrRaw);
  if (digits.length === MAX_DIGITS_10) return `+1${digits}`;
  if (digits.length === MAX_DIGITS_11 && digits.startsWith(US_COUNTRY_CODE)) return `+${digits}`;
  return displayOrRaw.trim();
}
